import type { ReactNode } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { PencilIcon, BuildingLibraryIcon } from "@heroicons/react/24/outline";

import { formatCurrency } from "helpers/currency";
import Button from "components/Button";

import { useArrangement } from "./ArrangementContext";
import { STEP_ROUTES } from "./constants";
import type { PackageSection } from "./screenModel";
import { formatVariantLabel } from "./variantLabels";
import {
  YES_ONLY_SERVICE_LABELS,
  deathCertCost,
  visiblePassThroughFeeAnswers,
  getIncludedOfferingQuantity,
  extractQuoteFees,
  filterVisibleServiceAnswers,
} from "./helpers";

type AdjustOrderDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function AdjustOrderDrawer({
  isOpen,
  onClose,
}: AdjustOrderDrawerProps) {
  const {
    state,
    quoteTotal,
    quoteBreakdown,
    isQuoteLoading,
    goToStep,
    packageInclusions,
    config,
  } = useArrangement();

  const packages = config?.packages ?? [];
  const urns = config?.urns ?? [];
  const keepsakes = config?.keepsakes ?? [];
  const serviceQuestions = config?.service_questions ?? [];
  const addOns = config?.add_ons ?? [];

  const visibleServiceAnswers = filterVisibleServiceAnswers(
    state.serviceAnswers,
    serviceQuestions,
  );
  const {
    items: quoteFeeItems,
    stateCountyFees,
    credits,
  } = extractQuoteFees(quoteBreakdown);
  const passThroughFeeAnswers = visiblePassThroughFeeAnswers(
    state.serviceAnswers,
  );

  const selectedPackage = packages.find(
    (p) => p.variant_id === state.selectedPackageId,
  );
  const includedUrn = urns.find((u) => u.id === state.includedUrnId);
  const includedVariant = includedUrn?.variants.find(
    (v) => v.id === state.includedUrnVariantId,
  );
  const includedVariantLabel = formatVariantLabel(includedVariant?.name);
  const additionalUrnDetails = state.additionalUrns.map((sel) => {
    const urn = urns.find((u) => u.id === sel.urnId);
    const variant = urn?.variants.find((v) => v.id === sel.variantId);
    return { ...sel, urn, variant };
  });
  const handleEdit = (step: number, section?: PackageSection) => {
    goToStep(step, section);
    onClose();
  };

  const hasSelections =
    selectedPackage ||
    includedUrn ||
    additionalUrnDetails.length > 0 ||
    state.selectedKeepsakes.length > 0 ||
    state.serviceAnswers.length > 0;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-evergreen/40 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer. The panel stays mounted so it can slide; apply the shadow
          only while open, otherwise shadow-2xl bleeds back over the right edge
          of the viewport while the panel sits off-screen (translate-x-full).
          pointer-events-none keeps the closed off-screen panel inert. */}
      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col transition-transform duration-300 ${
          isOpen
            ? "translate-x-0 shadow-2xl"
            : "pointer-events-none translate-x-full"
        }`}
      >
        {/* Header — fixed at top */}
        <div className="border-b border-oat bg-oat-50 px-6 py-5">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl italic text-evergreen">
              Your Arrangements
            </h2>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-soil transition hover:bg-oat"
              aria-label="Close drawer"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto bg-white px-6 py-5">
          {!hasSelections && (
            <p className="py-8 text-center text-lg text-soil">
              Your selections will appear here as you build your arrangement.
            </p>
          )}

          {/* Package */}
          {selectedPackage && (
            <Section
              title="Cremation Package"
              onEdit={() => handleEdit(STEP_ROUTES.indexOf("packages"))}
            >
              <LineItem
                label={`${selectedPackage.title} Package`}
                amount={parseFloat(selectedPackage.price)}
              />
            </Section>
          )}

          {/* Included Urn */}
          {includedUrn && (
            <Section
              title="Your Included Urn"
              onEdit={() => handleEdit(STEP_ROUTES.indexOf("packages"))}
            >
              <div className="flex items-center justify-between py-1">
                <span className="text-lg text-evergreen">
                  {includedUrn.name}
                  {includedVariantLabel && ` — ${includedVariantLabel}`}
                </span>
                <IncludedBadge />
              </div>
            </Section>
          )}

          {/* Additional Urns */}
          {additionalUrnDetails.length > 0 && (
            <Section
              title="Additional Urns"
              onEdit={() => handleEdit(STEP_ROUTES.indexOf("packages"))}
            >
              {additionalUrnDetails.map(({ urn, variant, quantity, urnId }) => {
                if (!urn) return null;
                const variantLabel = formatVariantLabel(variant?.name);
                return (
                  <LineItem
                    key={urnId}
                    label={`${urn.name}${variantLabel ? ` — ${variantLabel}` : ""}${quantity > 1 ? ` x${quantity}` : ""}`}
                    amount={(variant?.price ?? urn.price) * quantity}
                  />
                );
              })}
            </Section>
          )}

          {/* Keepsakes */}
          {state.selectedKeepsakes.length > 0 && (
            <Section
              title="Keepsakes"
              onEdit={() => handleEdit(STEP_ROUTES.indexOf("packages"))}
            >
              {state.selectedKeepsakes.map((sel) => {
                const keepsake = keepsakes.find((k) => k.id === sel.productId);
                if (!keepsake) return null;
                const keepsakeVariant = keepsake.variants?.find(
                  (v) => v.id === sel.variantId,
                );
                const variantLabel = formatVariantLabel(keepsakeVariant?.name);
                return (
                  <LineItem
                    key={sel.productId}
                    label={`${keepsake.name}${variantLabel ? ` — ${variantLabel}` : ""}${sel.quantity > 1 ? ` x${sel.quantity}` : ""}`}
                    amount={
                      (keepsakeVariant?.price ?? keepsake.price) * sel.quantity
                    }
                  />
                );
              })}
            </Section>
          )}

          {/* Additional Services */}
          {visibleServiceAnswers.length > 0 && (
            <Section
              title="Additional Services"
              onEdit={() => handleEdit(STEP_ROUTES.indexOf("services"))}
            >
              {visibleServiceAnswers.map((answer) => {
                const question = serviceQuestions.find(
                  (q) => q.id === answer.questionId,
                );
                if (!question) return null;

                if (question.type === "radio" && answer.selectedOptionId) {
                  const option = question.options?.find(
                    (o) => o.id === answer.selectedOptionId,
                  );
                  if (!option) return null;
                  const isIncluded =
                    !!option.offeringId &&
                    getIncludedOfferingQuantity(
                      packageInclusions,
                      option.offeringId,
                    ) > 0;
                  const price = isIncluded ? 0 : option.price;
                  const displayLabel =
                    YES_ONLY_SERVICE_LABELS[question.id] ?? option.label;
                  if (isIncluded) {
                    return (
                      <div
                        key={answer.questionId}
                        className="flex items-center justify-between py-1"
                      >
                        <span className="text-lg text-evergreen">
                          {displayLabel}
                        </span>
                        <IncludedBadge />
                      </div>
                    );
                  }

                  return (
                    <LineItem
                      key={answer.questionId}
                      label={displayLabel}
                      amount={price}
                    />
                  );
                }

                return null;
              })}
            </Section>
          )}

          {/* Add-On Services */}
          {state.addOnSelections.length > 0 && (
            <Section
              title="Add-On Services"
              onEdit={() =>
                handleEdit(STEP_ROUTES.indexOf("packages"), "add-ons")
              }
            >
              {state.addOnSelections.map((sel) => {
                const service = addOns.find((s) => s.id === sel.serviceId);
                if (!service || service.informationalOnly) return null;
                const includedQty = getIncludedOfferingQuantity(
                  packageInclusions,
                  service.offeringId,
                );
                const chargeableQty = Math.max(0, sel.quantity - includedQty);
                const amount = service.pricing.amount * chargeableQty;
                const isFullyIncluded = chargeableQty === 0 && includedQty > 0;
                const label =
                  sel.quantity > 1
                    ? `${service.name} x${sel.quantity}`
                    : service.name;
                if (isFullyIncluded) {
                  return (
                    <div
                      key={sel.serviceId}
                      className="flex items-center justify-between py-1"
                    >
                      <span className="text-lg text-evergreen">{label}</span>
                      <IncludedBadge />
                    </div>
                  );
                }
                return (
                  <LineItem key={sel.serviceId} label={label} amount={amount} />
                );
              })}
            </Section>
          )}

          {/* Government & Filing Fees */}
          {passThroughFeeAnswers.length > 0 && (
            <div className="mb-5 border-b border-oat/60 pb-5 last:mb-0 last:border-0 last:pb-0">
              <div className="mb-2.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <BuildingLibraryIcon className="h-4 w-4 text-soil" />
                  <h3 className="text-sm font-bold uppercase tracking-wide text-soil">
                    Government & Filing Fees
                  </h3>
                </div>
                <button
                  onClick={() => handleEdit(STEP_ROUTES.indexOf("services"))}
                  className="flex items-center gap-1 rounded-full border border-oat px-3 py-1 text-sm text-soil transition hover:border-soil/30 hover:text-evergreen"
                >
                  <PencilIcon className="h-3 w-3" />
                  Edit
                </button>
              </div>
              <p className="mb-2 text-xs text-soil">
                Collected on your behalf and paid to the issuing authority.
              </p>
              {passThroughFeeAnswers.map((answer) => {
                const question = serviceQuestions.find(
                  (q) => q.id === answer.questionId,
                );
                if (!question) return null;

                if (
                  question.type === "stepper" &&
                  answer.quantity &&
                  answer.quantity > 0 &&
                  question.stepperConfig
                ) {
                  const { firstCopyPrice, additionalCopyPrice } =
                    question.stepperConfig;
                  const included = packageInclusions?.includedDeathCerts ?? 0;
                  const total = deathCertCost(
                    answer.quantity,
                    included,
                    firstCopyPrice,
                    additionalCopyPrice,
                  );

                  if (total === 0 && included > 0) {
                    return (
                      <div
                        key={answer.questionId}
                        className="flex items-center justify-between py-1"
                      >
                        <span className="text-lg text-evergreen">
                          Death Certificates x{answer.quantity}
                        </span>
                        <IncludedBadge />
                      </div>
                    );
                  }

                  return (
                    <div key={answer.questionId}>
                      <LineItem
                        label={`Death Certificates x${answer.quantity}`}
                        amount={total}
                      />
                      {included > 0 && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <IncludedBadge size="sm" />
                          <span className="text-sm text-soil">
                            {included}x in plan
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              })}
            </div>
          )}
          {/* Quote-derived fees (weight, split, pacemaker, fingerprint, state/county) */}
          {(quoteFeeItems.length > 0 || stateCountyFees > 0) && (
            <div className="mb-5 border-b border-oat/60 pb-5 last:mb-0 last:border-0 last:pb-0">
              <h3 className="mb-2.5 text-sm font-bold uppercase tracking-wide text-soil">
                Additional Fees
              </h3>
              {quoteFeeItems.map((fee) => (
                <LineItem
                  key={fee.label}
                  label={fee.label}
                  amount={fee.amount}
                />
              ))}
              {stateCountyFees > 0 && (
                <LineItem
                  label="State & County Fees"
                  amount={stateCountyFees}
                />
              )}
            </div>
          )}

          {/* Credits for inclusions the family declined. The total below is
              already net of them, so they show as negative lines. */}
          {credits.length > 0 && (
            <div className="mb-5 border-b border-oat/60 pb-5 last:mb-0 last:border-0 last:pb-0">
              <h3 className="mb-2.5 text-sm font-bold uppercase tracking-wide text-soil">
                Credits
              </h3>
              {credits.map((credit) => (
                <LineItem
                  key={credit.label}
                  label={credit.label}
                  amount={-credit.amount}
                />
              ))}
            </div>
          )}
        </div>

        {/* Total — fixed at bottom */}
        <div className="border-t-2 border-evergreen bg-oat-50 px-6 py-5">
          <div className="flex items-baseline justify-between">
            <span className="font-serif text-xl italic text-evergreen">
              Total
            </span>
            {isQuoteLoading ? (
              <span className="text-lg text-soil">Calculating...</span>
            ) : (
              <span className="text-2xl font-bold text-evergreen">
                {formatCurrency(quoteTotal)}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function Section({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: ReactNode;
}) {
  return (
    <div className="mb-5 border-b border-oat/60 pb-5 last:mb-0 last:border-0 last:pb-0">
      <div className="mb-2.5 flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wide text-soil">
          {title}
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
          className="rounded-full"
          icon={<PencilIcon className="h-3 w-3" />}
        >
          Edit
        </Button>
      </div>
      {children}
    </div>
  );
}

function LineItem({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-lg text-evergreen">{label}</span>
      <span className="text-lg tabular-nums text-evergreen">
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

function IncludedBadge({ size = "md" }: { size?: "sm" | "md" }) {
  if (size === "sm") {
    return (
      <span className="inline-flex rounded-full bg-sage/15 px-2 py-0.5 text-xs font-medium text-sage">
        Included
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-sage/15 px-3 py-0.5 text-sm font-medium text-sage">
      Included
    </span>
  );
}
